import { cloneElement, type JSX } from 'react';
import { WebMenuChevronDownSvg } from '../../../icons/icons';
import { Dropdown } from '../../Dropdown/Dropdown';
import type { DiNavDropdownProps } from './DiNavDropdown.types';
import { DiNavDropdownMenu } from './DiNavDropdownMenu';

const DROPDOWN_OFFSET_OPTIONS = { offset: { mainAxis: 0 } };

/**
 * `DiNavDropdown` component designed specifically for use within the `DiNav` component,
 * provides a customizable dropdown menu that can adapt to different
 * navigation contexts such as HeaderNav and Tabs.
 *
 * @example
 *
 *```tsx
 * <DiNavDropdown
 *   label="More"
 *   items={overflowingItems}
 *   currentIndex={activeItemIndex}
 *   trigger={<HeaderNavItem font="hind" />}
 *   menuItem={<HeaderNavDropdownItem />}
 *   dropdownMenuClassName="custom-menu-styles"
 * />
 * ```
 */
export function DiNavDropdown({
  label,
  items,
  currentIndex = -1,
  options = DROPDOWN_OFFSET_OPTIONS,
  'data-testid': dataTestId,
  tabIndex,
  dropdownPlacement,
  trigger,
  dropdownMenuItem,
  dropdownMenuClassName,
}: DiNavDropdownProps): JSX.Element {
  return (
    <Dropdown
      data-testid={dataTestId}
      options={options}
      placement={dropdownPlacement}
      renderTrigger={({ triggerProps }) => {
        return cloneElement(trigger, {
          label,
          icon: <WebMenuChevronDownSvg />,
          iconPosition: 'right',
          isCurrent: currentIndex >= 0 && currentIndex < (items?.length ?? 0),
          ...(tabIndex && { tabIndex }),
          ...triggerProps,
        });
      }}
    >
      <DiNavDropdownMenu
        items={items}
        dropdownMenuItem={dropdownMenuItem}
        currentIndex={currentIndex}
        dropdownMenuClassName={dropdownMenuClassName}
      />
    </Dropdown>
  );
}
